import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { inputCls } from "./SectionEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ConciergeBrain({ token }) {
  const [prompt, setPrompt] = useState(null);
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios
      .get(`${API}/concierge-config`, { headers })
      .then((r) => {
        setPrompt(r.data.config?.system_prompt || "");
        setDefaultPrompt(r.data.default_prompt || "");
      })
      .catch(() => toast.error("Could not load concierge config"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (value) => {
    setSaving(true);
    try {
      await axios.put(`${API}/concierge-config`, { system_prompt: value }, { headers });
      toast.success(value ? "Concierge brain updated — live immediately." : "Reverted to the built-in brain.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (prompt === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={26} className="animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div data-testid="admin-concierge-brain">
      <h1 className="mb-2 font-display text-2xl font-semibold">Concierge Brain</h1>
      <p className="mb-6 max-w-2xl text-sm leading-relaxed text-slate-500">
        This is the instruction set the AI concierge follows when chatting with visitors — its knowledge, tone,
        and rules. Edit it and save; changes apply to new messages immediately. Leave empty to use the built-in brain.
      </p>

      <textarea
        data-testid="admin-brain-input"
        rows={16}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={defaultPrompt}
        className={`${inputCls} resize-y font-mono text-[13px] leading-relaxed`}
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          data-testid="admin-brain-save-btn"
          onClick={() => save(prompt)}
          disabled={saving}
          className="flex items-center gap-1.5 rounded-full bg-maple px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#C23225] disabled:opacity-60"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Brain
        </button>
        <button
          data-testid="admin-brain-reset-btn"
          onClick={() => { setPrompt(""); save(""); }}
          disabled={saving}
          className="flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 text-xs font-bold text-navy transition-colors hover:bg-navy hover:text-white disabled:opacity-60"
        >
          <RotateCcw size={13} /> Reset to Built-in
        </button>
      </div>

      <details className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <summary className="cursor-pointer text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
          View the built-in brain
        </summary>
        <pre className="mt-3 whitespace-pre-wrap font-mono text-xs leading-relaxed text-slate-500">{defaultPrompt}</pre>
      </details>
    </div>
  );
}
